import { getTokenFromStorage } from './api';

export class WebSocketService {
    private static instance: WebSocketService;
    private socket: WebSocket | null = null;
    private reconnectAttempts = 0;
    private maxReconnectAttempts = 5;
    private reconnectDelay = 1000;
    private isConnecting = false;
    private refreshToken = false;
    private skipNextCloseHandler = false;
    private onMaxReconnectFail?: () => Promise<void> | void;
    private heartbeatTimerId: number | null = null;
    private heartbeatIntervalMs = 60000; // 60s
    private onMessageHandler: ((message: any) => void) | null = null;

    private buildWsUrl(authToken: string): string {
        const rawBase = process.env.REACT_APP_SOCKET_DNS || '';
        const envPort = process.env.REACT_APP_SOCKET_PORT || '';

        // Garante protocolo
        let url: URL;
        try {
            url = new URL(rawBase);
        } catch {
            url = new URL(`wss://${rawBase}`);
        }

        const hasPortInHost = !!url.port;
        const hasPortInPath = /:(\d+)(?=\/|$)/.test(url.pathname);

        // Estratégia:
        // - Se já existe porta no host, mantém
        // - Senão, preferimos embutir a porta no PATH (compatível com reverse proxies que roteiam por ":port" no path)
        // - Só se já existir ":port" no path, não mexe
        if (!hasPortInHost && !hasPortInPath && envPort) {
            // Anexa ":<port>" ao fim do path atual
            const hasTrailingSlash = url.pathname.endsWith('/');
            const basePath = hasTrailingSlash ? url.pathname.slice(0, -1) : url.pathname;
            url.pathname = `${basePath}:${envPort}`;
        }

        url.searchParams.set('authToken', authToken);
        return url.toString();
    }

    public static getInstance(): WebSocketService {
        if (!WebSocketService.instance) {
            WebSocketService.instance = new WebSocketService();
        }
        return WebSocketService.instance;
    }

    public getSocket(): WebSocket | null {
        return this.socket;
    }

    public getMessage(callback: (message: any) => void): void {
        if (typeof callback !== 'function') return;
        // Guarda o callback para reaplicá-lo em futuras reconexões
        this.onMessageHandler = callback;
        if (this.socket) {
            this.socket.onmessage = callback;
        }
    }

    public setRefreshToken(isRefreshing: boolean): void {
        this.refreshToken = isRefreshing;
    }

    public setReconnectFailHandler(handler?: () => Promise<void> | void): void {
        this.onMaxReconnectFail = handler;
    }

    public async connect(): Promise<WebSocket | null> {
        if (this.socket?.readyState === WebSocket.OPEN) {
            console.log('[WebSocket] Conexão já está ativa');
            return this.socket;
        }

        if (this.isConnecting) {
            console.log('[WebSocket] Conexão em andamento, aguarde...');
            return null;
        }

        this.isConnecting = true;

        try {
            const webClientToken = getTokenFromStorage();
            const access_token: string = webClientToken.access_token;

            if (!access_token) {
                console.error('[WebSocket] Token não encontrado');
                return null;
            }

            console.log('[WebSocket] Iniciando conexão...');
            // const wsUrl = this.buildWsUrl(access_token);
            const wsUrl = `${process.env.REACT_APP_SOCKET_DNS}:${process.env.REACT_APP_SOCKET_PORT}?authToken=${access_token}`;
            if (this.socket) {
                console.log('[WebSocket] Fechando conexão anterior');
                this.socket.close();
                this.socket = null;
            }

            return await new Promise((resolve, reject) => {
                this.socket = new WebSocket(wsUrl);

                // Reaplica handler de mensagem imediatamente ao criar o socket
                if (this.onMessageHandler && this.socket) {
                    this.socket.onmessage = this.onMessageHandler;
                }

                const timeout = setTimeout(() => {
                    if (this.socket?.readyState !== WebSocket.OPEN) {
                        console.error('[WebSocket] Timeout na conexão');
                        this.socket?.close();
                        reject(new Error('Timeout na conexão do WebSocket'));
                    }
                }, 10000);

                this.socket.onopen = () => {
                    clearTimeout(timeout);
                    console.log('[WebSocket] Conexão estabelecida com sucesso');
                    this.reconnectAttempts = 0;
                    this.isConnecting = false;
                    // this.startHeartbeat();
                    // Garante que o handler esteja configurado após abertura
                    if (this.onMessageHandler && this.socket) {
                        this.socket.onmessage = this.onMessageHandler;
                    }
                    resolve(this.socket);
                };

                this.socket.onclose = async (event) => {
                    console.log('fechou');
                    // Evita reconexão quando fomos nós que fechamos para trocar o token/reconectar manualmente
                    if (this.refreshToken || this.skipNextCloseHandler) {
                        this.skipNextCloseHandler = false;
                        // this.stopHeartbeat();
                        return;
                    }

                    clearTimeout(timeout);
                    console.log('[WebSocket] Conexão fechada. Código:', event.code, 'Razão:', event.reason);
                    this.isConnecting = false;
                    // this.stopHeartbeat();
                    if (!event.wasClean) {
                        reject(new Error(`Conexão fechada inesperadamente. Código: ${event.code}`));
                    }
                    await this.handleReconnect();
                };

                this.socket.onerror = (error) => {
                    clearTimeout(timeout);
                    console.error('[WebSocket] Erro na conexão:', error);
                    this.isConnecting = false;
                    // this.stopHeartbeat();
                    reject(error);
                };
            });
        } catch (error) {
            console.error('[WebSocket] Erro ao conectar:', error);
            return null;
        } finally {
            this.isConnecting = false;
        }
    }

    private async handleReconnect(): Promise<void> {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.error('[WebSocket] Número máximo de tentativas de reconexão atingido');
            if (this.onMaxReconnectFail) {
                try {
                    await this.onMaxReconnectFail();
                } catch (err) {
                    console.error('[WebSocket] Falha ao executar rotina pós-esgotamento de tentativas:', err);
                }
            }
            return;
        }

        this.reconnectAttempts++;
        const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

        console.log(`[WebSocket] Tentativa ${this.reconnectAttempts} de ${this.maxReconnectAttempts}`);
        console.log(`[WebSocket] Próxima tentativa em ${delay}ms`);

        await new Promise((resolve) => setTimeout(resolve, delay));

        try {
            console.log('[WebSocket] Token atualizado, reconectando...');
            await this.connect();
        } catch (error) {
            console.error('[WebSocket] Erro na reconexão:', error);
            await this.handleReconnect();
        }
    }

    private startHeartbeat(): void {
        // this.stopHeartbeat();
        const socket = this.socket;
        if (!socket) return;

        const sendHeartbeat = () => {
            if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;
            try {
                this.socket.send('heartbeat');
            } catch (err) {
                console.warn('[WebSocket] Falha ao enviar heartbeat:', err);
            }
        };

        // envia um primeiro heartbeat logo ao abrir, depois mantém o intervalo
        sendHeartbeat();
        this.heartbeatTimerId = window.setInterval(sendHeartbeat, this.heartbeatIntervalMs);
    }

    private stopHeartbeat(): void {
        if (this.heartbeatTimerId != null) {
            window.clearInterval(this.heartbeatTimerId);
            this.heartbeatTimerId = null;
        }
    }

    public close(): void {
        if (this.socket) {
            console.log('[WebSocket] Fechando conexão manualmente');
            this.skipNextCloseHandler = true;
            try {
                this.socket.onclose = null as any;
            } catch {}
            this.socket.close();
            this.socket = null;
            // this.stopHeartbeat();
        }
    }
}

export const socketService = WebSocketService.getInstance();
